import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CardView from '../../../components/Redesing/card-view';
import { isForbiddenError } from '../../../hooks/chat/client';
import {
  CANDIDATES_CAP,
  LockCandidate,
  RentalLock,
  RentalLockInput,
  RentalLockRole,
  isRentalLocksUnsupportedError,
  toRentalLockInput,
  useRentalLocks,
} from '../../../hooks/hardware/useRentalLocks';

export const SHARED_LOCKS_HELP =
  "Opened and locked together with this unit's rentals. Used by the kayak trailer: a kayak maps its wing lock and the shared box.";

const ROLE_LABELS: Record<RentalLockRole, string> = { WING: 'Wing', BOX: 'Box' };
const ROLES: RentalLockRole[] = ['WING', 'BOX'];

/** A role ant may add later still shows, under its own name. */
const roleLabel = (role: string): string => ROLE_LABELS[role as RentalLockRole] ?? role;

const OPENS_AT_START = 'Opens at start';
const ON_DEMAND = 'On demand';

/** A candidate's name: label, else number, else what else identifies the lock. */
export const lockName = (lock: LockCandidate): string =>
  lock.label || (lock.number != null ? `#${lock.number}` : lock.vendorId || lock.id);

/** A mapped lock's name: label, else vendor id, else its hardware id. */
export const mappedLockName = (lock: RentalLock): string =>
  lock.label || lock.vendorId || lock.hardwareId;

const lockDetail = (lock: LockCandidate): string =>
  [lock.hardwareProduct?.name, lock.vendorId].filter(Boolean).join(' · ');

/**
 * A mapped lock that is no longer a candidate (moved system, product
 * changed), shaped like one so the picker still lists it and it can be
 * unmapped. Its connection is unknown: the mapping does not carry it.
 */
const asCandidate = (lock: RentalLock): LockCandidate => ({
  id: lock.hardwareId,
  oldId: null,
  label: lock.label,
  number: null,
  vendorId: lock.vendorId,
  isOnline: null,
  hardwareProduct: lock.productName ? { name: lock.productName } : null,
});

/** A lock added from the picker: no role yet, opened at rental start. */
const newEntry = (hardwareId: string): RentalLockInput => ({
  hardwareId,
  role: null,
  autoUnlock: true,
});

function OnlineDot({ isOnline }: { isOnline: boolean | null }) {
  const label =
    isOnline === null || isOnline === undefined
      ? 'Connection unknown'
      : isOnline
      ? 'Online'
      : 'Offline';
  const color =
    isOnline === null || isOnline === undefined
      ? 'grey.400'
      : isOnline
      ? 'success.main'
      : 'error.main';
  return (
    <Tooltip title={label}>
      <Box
        role="img"
        aria-label={label}
        sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
      />
    </Tooltip>
  );
}

function LockRow({ lock }: { lock: LockCandidate }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <OnlineDot isOnline={lock.isOnline} />
      <ListItemText
        primary={lockName(lock)}
        secondary={lockDetail(lock) || undefined}
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption' }}
        sx={{ my: 0 }}
      />
    </Stack>
  );
}

/**
 * A mapped lock in the card: its name, its role as a chip when it has one,
 * and its product and when it opens underneath.
 */
function MappedLockRow({ lock }: { lock: RentalLock }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ minWidth: 0, width: '100%' }}
    >
      <ListItemText
        primary={mappedLockName(lock)}
        secondary={[lock.productName, lock.autoUnlock ? OPENS_AT_START : ON_DEMAND]
          .filter(Boolean)
          .join(' · ')}
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption' }}
        sx={{ my: 0 }}
      />
      {lock.role ? (
        <Chip
          size="small"
          variant="outlined"
          label={roleLabel(lock.role)}
          data-id="shared-lock-role"
        />
      ) : null}
    </Stack>
  );
}

/** Same locks, same order, same role and opening: nothing to save. */
const sameMapping = (a: RentalLockInput[], b: RentalLockInput[]): boolean =>
  a.length === b.length &&
  a.every(
    (entry, i) =>
      entry.hardwareId === b[i].hardwareId &&
      entry.role === b[i].role &&
      entry.autoUnlock === b[i].autoUnlock
  );

const moveItem = <T,>(items: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

interface DialogProps {
  open: boolean;
  /** The mapping when the dialog opened, settings included: the draft starts from it. */
  initial: RentalLockInput[];
  /** The mapping as last read; only for rows that are no longer candidates. */
  locks: RentalLock[];
  candidates: LockCandidate[];
  candidatesTruncated: boolean;
  candidatesLoading: boolean;
  candidatesError: unknown;
  saving: boolean;
  saveError: unknown;
  onClose: () => void;
  /** The whole mapping, in the order the locks open. */
  onSave: (locks: RentalLockInput[]) => void;
}

const errorText = (error: unknown): string => {
  if (isForbiddenError(error)) {
    return "You do not have permission to change this unit's shared locks.";
  }
  if (isRentalLocksUnsupportedError(error)) {
    return 'The server cannot save these shared-lock settings yet. Nothing was saved.';
  }
  return error instanceof Error ? error.message : String(error);
};

/**
 * Picks the locks in the order they are opened. A checkbox adds a lock to
 * the end of the selection, with no role and opened at rental start; the
 * arrows reorder it, and each selected lock has its role and whether it
 * opens at start. A mapped lock that is no longer a candidate (moved
 * system, product changed) still shows, so it can be unmapped. Any
 * difference from the mapping as opened (a lock, the order, a role or an
 * opening) is an unsaved change that Reset undoes.
 */
export function SharedLocksDialog({
  open,
  initial,
  locks,
  candidates,
  candidatesTruncated,
  candidatesLoading,
  candidatesError,
  saving,
  saveError,
  onClose,
  onSave,
}: DialogProps) {
  /** The mapping being edited: each lock with its role and opening, in order. */
  const [draft, setDraft] = useState<RentalLockInput[]>([]);
  /** The `initial` the draft was last seeded from, so the seeding render is not "dirty". */
  const [seed, setSeed] = useState<RentalLockInput[] | null>(null);

  // Once per editing session: a background refresh of `locks` while the
  // dialog is open (a label or a setting changed elsewhere) must not wipe
  // unsaved edits.
  useEffect(() => {
    if (open) {
      setDraft(initial);
      setSeed(initial);
    }
  }, [open, initial]);

  const dirty = seed === initial && !sameMapping(draft, initial);

  const options = useMemo(
    () => [
      ...candidates,
      ...locks
        .filter((lock) => !candidates.some((c) => c.id === lock.hardwareId))
        .map(asCandidate),
    ],
    [candidates, locks]
  );
  const byId = useMemo(
    () => new Map(options.map((lock) => [lock.id, lock] as const)),
    [options]
  );

  const isSelected = (id: string) => draft.some((entry) => entry.hardwareId === id);

  const toggle = (id: string) =>
    setDraft((current) =>
      current.some((entry) => entry.hardwareId === id)
        ? current.filter((entry) => entry.hardwareId !== id)
        : [...current, newEntry(id)]
    );

  const update = (id: string, patch: Partial<Omit<RentalLockInput, 'hardwareId'>>) =>
    setDraft((current) =>
      current.map((entry) => (entry.hardwareId === id ? { ...entry, ...patch } : entry))
    );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Shared locks</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {SHARED_LOCKS_HELP}
        </Typography>
        {saveError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorText(saveError)}
          </Alert>
        ) : null}
        <Typography variant="subtitle2">Locks in this system</Typography>
        {candidatesTruncated ? (
          <Typography variant="caption" color="text.secondary" display="block">
            Showing the first {CANDIDATES_CAP} locks of this system.
          </Typography>
        ) : null}
        {candidatesLoading ? (
          <CircularProgress size={20} aria-label="Loading locks" sx={{ my: 1 }} />
        ) : options.length === 0 ? (
          candidatesError ? (
            <Alert severity="warning" sx={{ my: 1 }}>
              Unable to load the locks of this system. {errorText(candidatesError)}
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              No hardware in this system can be opened remotely.
            </Typography>
          )
        ) : (
          <>
            {candidatesError ? (
              // A refresh failed; the list read before stays usable.
              <Alert severity="warning" sx={{ my: 1 }}>
                Unable to refresh the locks of this system. {errorText(candidatesError)}
              </Alert>
            ) : null}
            <FormGroup sx={{ mb: 2 }}>
              {options.map((lock) => (
                <FormControlLabel
                  key={lock.id}
                  control={
                    <Checkbox
                      checked={isSelected(lock.id)}
                      onChange={() => toggle(lock.id)}
                      inputProps={{ 'aria-label': lockName(lock) }}
                    />
                  }
                  label={<LockRow lock={lock} />}
                />
              ))}
            </FormGroup>
          </>
        )}
        <Typography variant="subtitle2">Selected (in order)</Typography>
        {draft.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
            None. Saving removes the mapping.
          </Typography>
        ) : (
          <List dense disablePadding aria-label="Selected locks">
            {draft.map((entry, index) => {
              const lock = byId.get(entry.hardwareId);
              const name = lock ? lockName(lock) : entry.hardwareId;
              return (
                <ListItem
                  key={entry.hardwareId}
                  disableGutters
                  divider={index < draft.length - 1}
                  data-id="shared-lock-draft"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      columnGap: 1.5,
                      rowGap: 0.5,
                      width: '100%',
                      py: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ flex: '1 1 8rem', minWidth: 0, overflowWrap: 'anywhere' }}
                    >
                      {`${index + 1}. ${name}`}
                    </Typography>
                    <TextField
                      select
                      size="small"
                      label="Role"
                      value={entry.role ?? ''}
                      onChange={(event) =>
                        update(entry.hardwareId, {
                          role: (event.target.value || null) as RentalLockRole | null,
                        })
                      }
                      sx={{ width: 112 }}
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{
                        displayEmpty: true,
                        SelectDisplayProps: { 'aria-label': `Role of ${name}` },
                      }}
                    >
                      <MenuItem value="">None</MenuItem>
                      {ROLES.map((role) => (
                        <MenuItem key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </MenuItem>
                      ))}
                      {entry.role && !ROLES.includes(entry.role) ? (
                        // A role ant added after this page: kept, and shown by name.
                        <MenuItem value={entry.role}>{roleLabel(entry.role)}</MenuItem>
                      ) : null}
                    </TextField>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={entry.autoUnlock}
                          onChange={(event) =>
                            update(entry.hardwareId, { autoUnlock: event.target.checked })
                          }
                          inputProps={{
                            role: 'switch',
                            'aria-label': `${name} opens at start`,
                          }}
                        />
                      }
                      label={<Typography variant="body2">{OPENS_AT_START}</Typography>}
                    />
                    <Stack direction="row">
                      <IconButton
                        size="small"
                        aria-label={`Move ${name} up`}
                        disabled={index === 0}
                        onClick={() =>
                          setDraft((current) => moveItem(current, index, index - 1))
                        }
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Move ${name} down`}
                        disabled={index === draft.length - 1}
                        onClick={() =>
                          setDraft((current) => moveItem(current, index, index + 1))
                        }
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        {dirty ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: 'auto', pl: 1 }}
            data-id="shared-locks-dirty"
          >
            Unsaved changes
          </Typography>
        ) : null}
        <Button
          sx={{ float: 'none' }}
          onClick={() => setDraft(initial)}
          disabled={!dirty || saving}
        >
          Reset
        </Button>
        <Button sx={{ float: 'none' }} onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          sx={{ float: 'none' }}
          variant="contained"
          disabled={saving}
          onClick={() => onSave(draft)}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface Props {
  hardwareId: string;
  systemId: string | null | undefined;
  /** The same right as the page's Edit button: physical access or Movatic staff. */
  canEdit: boolean;
}

/**
 * The locks a unit opens with its rentals, in order. Renders nothing until
 * ant confirms it has the fields, nothing at all when it does not, and
 * nothing for a unit whose product does not opt in unless a mapping is
 * already there (a legacy one, shown so it can be cleared). A transient
 * failure shows inline, with the last answer kept, and never takes an open
 * dialog down.
 */
export default function SharedLocks({ hardwareId, systemId, canEdit }: Props) {
  const [open, setOpen] = useState(false);
  /** The mapping at the moment Edit was pressed; stable while the dialog is open. */
  const [draftStart, setDraftStart] = useState<RentalLockInput[]>([]);
  const {
    locks,
    loaded,
    hidden,
    loading,
    fetching,
    unsupported,
    error,
    retry,
    candidates,
    candidatesTruncated,
    candidatesLoading,
    candidatesError,
    setRentalLocks,
    saving,
    saveError,
    resetSaveError,
  } = useRentalLocks(hardwareId, systemId, { candidatesEnabled: open });

  // Nothing at all before ant's first answer (it may not have the fields),
  // nor for a unit the feature does not apply to; after an answer that
  // applies the card stays mounted, so a Retry never takes an open dialog down.
  if (unsupported || loading || hidden) return null;

  const openDialog = () => {
    // Only a mapping that was read can be edited: an empty draft from a
    // failed read would clear the real one on Save.
    if (!loaded) return;
    resetSaveError();
    setDraftStart(locks.map(toRentalLockInput));
    setOpen(true);
  };

  const save = async (entries: RentalLockInput[]) => {
    try {
      await setRentalLocks(entries);
      setOpen(false);
    } catch (error) {
      // Shown inline by the dialog through saveError.
    }
  };

  return (
    <>
      <CardView
        title="Shared locks"
        dataId="shared-locks"
        headerActions={
          // A plain Button, so the global `button { float: right }` is turned off here.
          canEdit ? (
            <Button
              sx={{ float: 'none' }}
              size="small"
              startIcon={<EditIcon />}
              onClick={openDialog}
              disabled={!loaded}
              data-id="shared-locks-edit"
            >
              Edit
            </Button>
          ) : undefined
        }
        content={
          <Stack spacing={1} sx={{ px: 2, pb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {SHARED_LOCKS_HELP}
            </Typography>
            {error ? (
              <Alert
                severity="warning"
                action={
                  <Button sx={{ float: 'none' }} size="small" onClick={() => retry()}>
                    Retry
                  </Button>
                }
              >
                Unable to {loaded ? 'refresh' : 'load'} the shared locks.
              </Alert>
            ) : null}
            {!loaded && fetching ? (
              <CircularProgress size={16} aria-label="Loading shared locks" />
            ) : null}
            {!loaded ? null : locks.length === 0 ? (
              <Typography variant="body2" data-id="shared-locks-empty">
                No shared locks.
              </Typography>
            ) : (
              <List dense disablePadding aria-label="Shared locks">
                {locks.map((lock, index) => (
                  <ListItem
                    key={lock.hardwareId}
                    disableGutters
                    divider={index < locks.length - 1}
                    data-id="shared-lock"
                  >
                    <MappedLockRow lock={lock} />
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>
        }
      />
      {canEdit ? (
        <SharedLocksDialog
          open={open}
          initial={draftStart}
          locks={locks}
          candidates={candidates}
          candidatesTruncated={candidatesTruncated}
          candidatesLoading={candidatesLoading}
          candidatesError={candidatesError}
          saving={saving}
          saveError={saveError}
          onClose={() => setOpen(false)}
          onSave={save}
        />
      ) : null}
    </>
  );
}
